import React, { useState, useEffect, useCallback } from 'react';
import { Modal, List, Button, Avatar, Select, message as antdMessage, Tag, Spin } from 'antd';
import { UserDeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import axios from 'axios';
import _debounce from 'lodash/debounce';

const ChatSettingsModal = ({ visible, onClose, conversation, currentUserId, onParticipantsUpdate }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSearchOptions, setUserSearchOptions] = useState([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (conversation && conversation.participants) {
      setParticipants(conversation.participants);
    } else {
      setParticipants([]);
    }
  }, [conversation]);

  const isCurrentUserCreator = conversation?.createdBy?._id === currentUserId;
  // Assuming admin role is passed or fetched elsewhere, for now, only creator can manage
  // TODO: Add admin role check from a context or user object if available
  const canManageParticipants = isCurrentUserCreator; // Or: isCurrentUserCreator || currentUser.isAdmin;

  const handleSearchUsers = useCallback(_debounce(async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setUserSearchOptions([]);
      return;
    }
    setIsSearching(true);
    try {
      const token = localStorage.getItem('authToken'); // Use correct token key from AuthContext
      // Assuming a general user search endpoint exists or is created
      // For now, using /users?search=searchText (without /api/v1 prefix)
      const response = await axios.get(`/users?search=${searchText}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const users = response.data.data.users || response.data.data; // Adjust based on actual API response
      const options = users
        .filter(user => !participants.find(p => p._id === user._id)) // Exclude current participants
        .map(user => ({
          value: user._id,
          label: `${user.name} (${user.email || user.role || 'User'})`,
        }));
      setUserSearchOptions(options);
    } catch (error) {
      console.error('Error searching users:', error);
      antdMessage.error('Failed to search users.');
      setUserSearchOptions([]);
    } finally {
      setIsSearching(false);
    }
  }, 500), [participants]); // Debounce for 500ms, re-create if participants change

  const handleAddParticipant = async () => {
    if (!selectedUserToAdd || !conversation?._id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken'); // Use correct token key from AuthContext
      const response = await axios.put(
        `/chat/conversations/${conversation._id}/participants`, // Remove /api/v1 prefix
        { userIdsToAdd: [selectedUserToAdd.value] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParticipants(response.data.data.participants); // Update local state
      antdMessage.success(`${selectedUserToAdd.label} added to conversation.`);
      setSelectedUserToAdd(null);
      setUserSearchOptions([]);
      if (onParticipantsUpdate) onParticipantsUpdate(response.data.data); // Notify parent
    } catch (error) {
      console.error('Error adding participant:', error);
      antdMessage.error('Failed to add participant.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipant = async (userIdToRemove) => {
    if (!conversation?._id || userIdToRemove === conversation?.createdBy?._id) {
        antdMessage.warn("Creator cannot be removed from the conversation by this action."); // Or handle differently
        return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken'); // Use correct token key from AuthContext
      const response = await axios.put(
        `/chat/conversations/${conversation._id}/participants`, // Remove /api/v1 prefix
        { userIdsToRemove: [userIdToRemove] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParticipants(response.data.data.participants);
      antdMessage.success('Participant removed.');
      if (onParticipantsUpdate) onParticipantsUpdate(response.data.data); // Notify parent
    } catch (error) {
      console.error('Error removing participant:', error);
      antdMessage.error('Failed to remove participant.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal
      title="Manage Participants"
      visible={visible}
      onCancel={onClose}
      footer={null} // Or custom footer if needed
      confirmLoading={loading}
    >
      {canManageParticipants && (
        <div style={{ marginBottom: '20px' }}>
          <h4>Add Participant</h4>
          <Select
            showSearch
            value={selectedUserToAdd}
            placeholder="Search users to add"
            style={{ width: '100%' }}
            defaultActiveFirstOption={false}
            showArrow={false}
            filterOption={false}
            onSearch={handleSearchUsers}
            onChange={(value, option) => setSelectedUserToAdd(option)}
            notFoundContent={isSearching ? <Spin size="small" /> : null}
            options={userSearchOptions}
            labelInValue
          />
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={handleAddParticipant}
            disabled={!selectedUserToAdd || loading}
            style={{ marginTop: '10px' }}
            loading={loading && !!selectedUserToAdd}
          >
            Add User
          </Button>
        </div>
      )}

      <h4>Current Participants ({participants.length})</h4>
      <List
        itemLayout="horizontal"
        dataSource={participants}
        renderItem={user => (
          <List.Item
            actions={canManageParticipants && user._id !== conversation?.createdBy?._id && user._id !== currentUserId ? 
                [<Button type="text" danger icon={<UserDeleteOutlined />} onClick={() => handleRemoveParticipant(user._id)} disabled={loading} />] 
                : (user._id === conversation?.createdBy?._id ? [<Tag color="gold">Creator</Tag>] : null)
            }
          >
            <List.Item.Meta
              avatar={<Avatar>{user.name?.substring(0, 1).toUpperCase() || 'U'}</Avatar>}
              title={user.name}
              description={user.role || 'Participant'}
            />
          </List.Item>
        )}
      />
    </Modal>
  );
};

export default ChatSettingsModal;
